import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import { Box } from '@mui/material';
import { amber } from '@mui/material/colors';
import { useEffect, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import useApi from '../../../lib/useApi';
import InfoBlock from '../../InfoBlock/InfoBlock';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import ErrorInfoBlock from './ErrorInfoBlock';

/**
 * YearlyLapseInfoBlock Component
 *
 * Fetches the yearly lapse date from the backend and displays it using the InfoBlock component.
 * Shows loading and error states as necessary.
 */
const YearlyLapseInfoBlock = () => {
  // State variables for the lapse date, loading, and error states
  const [yearlyLapseDate, setYearlyLapseDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // Initialize the ManagementApi using the custom useApi hook
  const managementApi = useApi(ManagementApi);

  // Fetch the yearly lapse date when the component mounts
  useEffect(() => {
    setIsLoading(true);
    setError(false);
    const fetchYearlyLapseDate = async () => {
      try {
        // Fetch the date from the API
        const date = await managementApi.GetYearlyLapseDate();

        // Format the date as "Month Day, Year" (e.g., June 16, 2024)
        const formattedDate = date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });

        setYearlyLapseDate(formattedDate);
      } catch (err) {
        console.error('Error fetching yearly lapse date:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchYearlyLapseDate();
  }, [managementApi]);

  // Render the InfoBlock with the fetched yearly lapse date
  return (
    <Box position="relative" height="100%" width="100%">
      {!error ? (
        <InfoBlock
          baseId="yearly-lapse"
          title="Yearly Lapse"
          content={yearlyLapseDate!} // Non-null assertion since we've handled null cases
          icon={<AutorenewRoundedIcon sx={{ height: '28px', width: '28px' }} />}
          subtitle="On this date, all member training progress will reset to 0%."
          color={amber.A400}
        />
      ) : (
        <ErrorInfoBlock
          baseId="yearly-lapse-error"
          title="Yearly Lapse"
          subtitle="Failed to load the yearly lapse date."
        />
      )}
      <LoadingCircle isLoading={isLoading} hideText />
    </Box>
  );
};

export default YearlyLapseInfoBlock;
